import React from 'react';

import PropTypes from 'prop-types';
import MapMarker from 'react-icons/lib/fa/map-marker';

import {
    TooltipController,
    TooltipSelect,
} from 'wanderu-components';
import { combineClasses } from 'wanderu-css';
import {
    localize,
} from 'wanderu-i18n';
import {
    CaretRight,
    Circle,
    Line,
    Moon,
} from 'wanderu-icons';

// we reference some depart/arrive classes like styles[foo] instead of styles.foo
// eslint-disable-next-line css-modules/no-unused-class
import styles from './Endpoint.scss';

const Station = (props) => {
    const {
        classes,
        name,
        onStationNameClicked,
        type,
    } = props;

    const handleClick = () => onStationNameClicked({
        name,
        type,
    });

    return (
        <div className={`${classes.station} ${classes[type]}`} onClick={handleClick}>
            {name}
        </div>
    );
};

Station.propTypes = {
    classes: PropTypes.object,
    name: PropTypes.string,
    onStationNameClicked: PropTypes.func,
    type: PropTypes.string,
};

const LocalizedStation = localize(Station);

const AlternateLocation = (props) => {
    const {
        cityName,
        classes,
        onNearbyLocationClicked,
        region,
        t,
        type,
    } = props;

    const handleClick = () => onNearbyLocationClicked({
        cityName,
        region,
        type,
    });

    return (
        <div className={classes.alternateLocationContainer} onClick={handleClick}>
            <TooltipController
                offsetX="center"
            >
                <div className={classes.tooltip}>
                    <MapMarker className={classes.alternateMarker} />
                    {t('baseApp:nearby-location')}
                </div>
                <TooltipSelect className={classes.alternateTooltipSelect}>
                    <MapMarker className={classes.alternateMarker} />
                    <div className={`${classes.alternateRegion} ${classes[type]}`}>
                        {cityName}, {region}
                    </div>
                </TooltipSelect>
            </TooltipController>
        </div>
    );
};

AlternateLocation.propTypes = {
    cityName: PropTypes.string,
    classes: PropTypes.object,
    onNearbyLocationClicked: PropTypes.func,
    region: PropTypes.string,
    t: PropTypes.func,
    type: PropTypes.string,
};

const LocalizedAlternateLocation = localize(AlternateLocation);

function DirectionArrow(props) {
    const {
        classes,
        duration,
        transfers,
    } = props;

    return (
        <div
            className={classes.arrowContainer}
            aria-hidden
        >
            {duration}
            <Line className={classes.line} />
            <CaretRight className={classes.caretRight} />
            {transfers > 0 && <Circle className={classes.transferDot} />}
        </div>
    );
}

DirectionArrow.propTypes = {
    classes: PropTypes.object,
    duration: PropTypes.element,
    transfers: PropTypes.number,
};

function Endpoint(props) {
    const {
        cityName,
        className = '',
        classes: classesProp,
        duration = null,
        i18nMoment,
        localizedDateTime,
        name,
        nearby,
        numberOfMidnightsPassed,
        onNearbyLocationClicked,
        onStationNameClicked,
        region,
        t,
        transfers,
        type,
    } = props;

    const arriveMoment = i18nMoment.parseZone(localizedDateTime);
    const classes = combineClasses(classesProp, styles);

    return (
        <div className={className}>
            <div className={classes.timeContainer}>
                <div
                    aria-label={type}
                    aria-live="polite"
                    className={`${classes.time} ${classes[type]}`}
                >
                    <span data-testid="formatted-time">{i18nMoment.parseZone(localizedDateTime).format('LT')}</span>
                    {type === 'arrive' && numberOfMidnightsPassed > 0 && (
                        <React.Fragment>
                            <span className={classes.multipleDaysText}>
                                +{numberOfMidnightsPassed}
                            </span>
                            <Moon
                                className={classes.multipleDaysIcon}
                                title={t('baseApp:next-day-arrival', {
                                    date: arriveMoment.shortDateTimestamp(),
                                })}
                            />
                        </React.Fragment>
                    )}
                </div>
                {type === 'depart' && (
                    <DirectionArrow
                        classes={classes}
                        duration={duration}
                        transfers={transfers}
                    />
                )}
            </div>
            <LocalizedStation
                classes={classes}
                name={name}
                onStationNameClicked={onStationNameClicked}
                type={type}
            />
            {nearby && (
                <LocalizedAlternateLocation
                    cityName={cityName}
                    classes={classes}
                    onNearbyLocationClicked={onNearbyLocationClicked}
                    region={region}
                    type={type}
                />
            )}
        </div>
    );
}

Endpoint.propTypes = {
    cityName: PropTypes.string.isRequired,
    classes: PropTypes.object,
    className: PropTypes.string,
    duration: PropTypes.element,
    i18nMoment: PropTypes.func,
    localizedDateTime: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    nearby: PropTypes.bool.isRequired,
    numberOfMidnightsPassed: PropTypes.number.isRequired,
    onNearbyLocationClicked: PropTypes.func.isRequired,
    onStationNameClicked: PropTypes.func.isRequired,
    region: PropTypes.string.isRequired,
    t: PropTypes.func,
    transfers: PropTypes.number,
    type: PropTypes.oneOf([
        'arrive',
        'depart',
    ]).isRequired,
};

export {
    Endpoint,
};
export default localize(Endpoint);
